import React from 'react';
import { Link } from 'react-router-dom';
import useFadeIn from '../hooks/useFadeIn';
import { buildSignupPath, buildSignupState, captureCtaClick, captureResourceOpen } from '../utils/analytics';

export default function ResourcesPage() {
  const [heroRef, heroVisible] = useFadeIn(0.05);
  const [cardRef, cardVisible] = useFadeIn(0.08);

  return (
    <div className="bg-white">

      {/* ── Hero ──────────────────────────────────── */}
      <section className="bg-[#f5f5f7] px-8 md:px-16 pt-14 pb-8">
        <div
          ref={heroRef}
          className={`fade-in-section ${heroVisible ? 'is-visible' : ''} max-w-[860px] mx-auto`}
        >
          <p className="text-[13px] text-[#86868b] tracking-widest uppercase font-medium mb-5">
            Resources
          </p>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#1d1d1f] tracking-tight leading-tight mb-4 whitespace-nowrap">
            Prescription coverage, explained.
          </h1>
          <p className="text-lg text-[#86868b] max-w-xl leading-relaxed font-light">
            Clinical guides and policy updates curated for healthcare providers — helping you stay current and act with confidence.
          </p>
        </div>
      </section>

      {/* ── Article List ──────────────────────────── */}
      <section
        ref={cardRef}
        className={`fade-in-section ${cardVisible ? 'is-visible' : ''} px-8 md:px-16 pt-6 pb-10 max-w-[860px] mx-auto`}
      >
        <div className="divide-y divide-[#e5e5ea]">

          {/* GLP-1 */}
          <Link
            to="/resources/glp1-bridge"
            className="group block py-8"
            onClick={() =>
              captureResourceOpen('glp1-bridge', {
                resource_title: 'Medicare GLP-1 Bridge Resource Center',
                resource_type: 'resource-center',
                source: 'resources-page',
              })
            }
          >
            {/* Meta row */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-green-100 text-green-700">
                Resource Center
              </span>
              <span className="text-xs text-[#86868b]">Source checked June 24, 2026</span>
              <span className="text-xs text-[#86868b]">·</span>
              <span className="text-xs text-[#86868b]">Guide · recording · assistant</span>
            </div>

            {/* Title */}
            <h2 className="text-xl md:text-[22px] font-semibold text-[#1d1d1f] leading-snug mb-2">
              Medicare GLP-1 Bridge Resource Center
            </h2>

            {/* Description */}
            <p className="text-[15px] text-[#86868b] leading-relaxed mb-4">
              Medicare is covering Foundayo, Wegovy, and Zepbound (KwikPen® only) for weight loss at a flat $50 copay/month from July 1, 2026 through December 31, 2027. Start with the guide, watch the recording, or open the provider assistant.
            </p>

            {/* Link */}
            <span className="inline-flex items-center gap-1 text-[14px] font-medium text-[#06c] group-hover:text-[#004aad] transition-colors">
              Explore resource center
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7v10"/>
              </svg>
            </span>
          </Link>

          {/* M3P */}
          <Link
            to="/resources/m3p"
            className="group block py-8"
            onClick={() =>
              captureResourceOpen('m3p', {
                resource_title: 'Medicare Prescription Payment Plan (M3P): Overview & FAQs',
                resource_type: 'clinical-guide',
                source: 'resources-page',
              })
            }
          >
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                Clinical Guide
              </span>
              <span className="text-xs text-[#86868b]">Updated Jan 2026</span>
              <span className="text-xs text-[#86868b]">·</span>
              <span className="text-xs text-[#86868b]">15 min read</span>
            </div>
            <h2 className="text-xl md:text-[22px] font-semibold text-[#1d1d1f] leading-snug mb-2">
              Medicare Prescription Payment Plan (M3P): Overview & FAQs
            </h2>
            <p className="text-[15px] text-[#86868b] leading-relaxed mb-4">
              Starting January 2025, all Medicare Part D plans must offer M3P — letting patients spread prescription costs into capped monthly payments instead of large lump sums. Learn who benefits, how to opt in, and how billing works.
            </p>
            <span className="inline-flex items-center gap-1 text-[14px] font-medium text-[#06c] group-hover:text-[#004aad] transition-colors">
              Read guide
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7M17 7H7M17 7v10"/>
              </svg>
            </span>
          </Link>

        </div>
      </section>

      {/* ── CTA strip ─────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#1d1d1f] px-8 md:px-16 py-12">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-blue-600/15 via-purple-600/10 to-transparent blur-3xl pointer-events-none" />
        <div className="relative max-w-[860px] mx-auto flex flex-col md:flex-row items-center justify-between gap-8">
          <div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Want coverage guidance in your workflow?
            </h3>
            <p className="text-[15px] text-white/50 font-light">
              MedHug surfaces real-time formulary and coverage data at the point of care.
            </p>
          </div>
          <Link
            to={buildSignupPath()}
            state={buildSignupState('resources-book-demo')}
            data-ph-capture-attribute-cta="resources-book-demo"
            onClick={() => captureCtaClick('resources-book-demo', { placement: 'resources-cta' })}
            className="bg-white text-[#1d1d1f] px-8 py-4 rounded-full text-[15px] font-medium hover:bg-gray-100 transition-all whitespace-nowrap flex-shrink-0 shadow-lg"
          >
            Book a Demo
          </Link>
        </div>
      </section>

    </div>
  );
}
